import { apiClient, getAuthToken } from "../../api/client";
import {
  HEARTBEAT_CATALOG_ID,
  HEARTBEAT_TARGETS,
  parseAirshowHeartbeatModelsPage,
  parseAirshowHeartbeatReport,
  parseAirshowHeartbeatRun,
  type AirshowHeartbeatModelsPage,
  type AirshowHeartbeatReport,
  type AirshowHeartbeatRun,
} from "./airshowHeartbeatContract";
import type { HeartbeatPollInput, HeartbeatPollResult } from "./airshowHeartbeatPolling";
import { LOOM_AIRSHOW_ARCHIVE_SHA256, LOOM_AIRSHOW_CATALOG_REVISION } from "./loomAirshowPresetTrust";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

export class AirshowHeartbeatCorruptResponse extends Error {
  readonly responseCause: unknown;

  constructor(cause?: unknown) {
    super("Airshow heartbeat response failed strict contract validation");
    this.name = "AirshowHeartbeatCorruptResponse";
    this.responseCause = cause;
  }
}

export interface HeartbeatIdentity {
  projectId: string;
  runId: string;
  signal?: AbortSignal;
}
export interface AirshowHeartbeatClient {
  current(input: { projectId: string; signal?: AbortSignal }): Promise<AirshowHeartbeatRun | null>;
  status(input: HeartbeatPollInput & { projectId: string }): Promise<HeartbeatPollResult>;
  start(input: {
    projectId: string; scope: "all" | "failures-from"; priorRunId: string | null;
    catalogRevision: string; archiveSha256: string; signal?: AbortSignal;
  }): Promise<AirshowHeartbeatRun>;
  cancel(input: HeartbeatIdentity): Promise<AirshowHeartbeatRun>;
  report(input: HeartbeatIdentity): Promise<AirshowHeartbeatReport>;
  models(input: HeartbeatIdentity & { cursor: number; limit: number }): Promise<AirshowHeartbeatModelsPage>;
}

function base(projectId: string): string {
  if (!projectId.trim()) throw new Error("Airshow heartbeat requires a project identity");
  return `/thread/v1/projects/${encodeURIComponent(projectId)}/aero/vsp/airshow/heartbeat-runs`;
}

function decode<T>(parser: () => T): T {
  try {
    return parser();
  } catch (error) {
    throw new AirshowHeartbeatCorruptResponse(error);
  }
}

function strongEtag(value: string | null): string | null {
  if (value === null) return null;
  if (!/^"sha256:[a-f0-9]{64}"$/.test(value)) throw new AirshowHeartbeatCorruptResponse();
  return value;
}

async function errorDetail(response: Response): Promise<string> {
  try {
    const body = await response.clone().json() as { detail?: unknown };
    if (typeof body.detail === "string") return body.detail;
  } catch {
    // Text fallback below.
  }
  return response.text().catch(() => "Heartbeat request failed");
}

async function getResponse(path: string, input: { signal?: AbortSignal; etag?: string | null }): Promise<Response> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (input.etag) headers["If-None-Match"] = input.etag;
  return fetch(`${API_BASE}${path}`, {
    method: "GET", credentials: "include", headers, signal: input.signal,
  });
}

export async function fetchCurrentAirshowHeartbeat(input: {
  projectId: string; signal?: AbortSignal;
}): Promise<AirshowHeartbeatRun | null> {
  const response = await getResponse(`${base(input.projectId)}/current`, input);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Airshow heartbeat current failed (${response.status}): ${await errorDetail(response)}`);
  const value: unknown = await response.json();
  return decode(() => parseAirshowHeartbeatRun(value, { projectId: input.projectId }));
}

export async function fetchAirshowHeartbeatStatus(input: HeartbeatPollInput & {
  projectId: string;
}): Promise<HeartbeatPollResult> {
  const response = await getResponse(`${base(input.projectId)}/${encodeURIComponent(input.runId)}`, input);
  const etag = strongEtag(response.headers.get("etag"));
  if (response.status === 304) {
    if (etag === null) throw new AirshowHeartbeatCorruptResponse();
    return { kind: "not-modified", etag };
  }
  if (!response.ok) throw new Error(`Airshow heartbeat status failed (${response.status}): ${await errorDetail(response)}`);
  const value: unknown = await response.json();
  return {
    kind: "status",
    status: decode(() => parseAirshowHeartbeatRun(value, {
      projectId: input.projectId, runId: input.runId,
    })),
    etag,
  };
}

export async function startAirshowHeartbeat(input: {
  projectId: string; scope: "all" | "failures-from"; priorRunId: string | null;
  catalogRevision: string; archiveSha256: string; signal?: AbortSignal;
}): Promise<AirshowHeartbeatRun> {
  if (input.catalogRevision !== LOOM_AIRSHOW_CATALOG_REVISION
      || input.archiveSha256 !== LOOM_AIRSHOW_ARCHIVE_SHA256
      || (input.scope === "failures-from") !== (input.priorRunId !== null)) {
    throw new Error("Airshow heartbeat start identity or retry scope is invalid");
  }
  const value = await apiClient<unknown>(base(input.projectId), {
    method: "POST", signal: input.signal,
    body: {
      schema: "loom.airshow-heartbeat-start-request/v1",
      catalog_id: HEARTBEAT_CATALOG_ID,
      catalog_revision: input.catalogRevision,
      archive_sha256: input.archiveSha256,
      scope: input.scope,
      prior_run_id: input.priorRunId,
      targets: [...HEARTBEAT_TARGETS],
    },
  });
  return decode(() => parseAirshowHeartbeatRun(value, {
    projectId: input.projectId,
    catalogRevision: input.catalogRevision,
    archiveSha256: input.archiveSha256,
  }));
}

export async function cancelAirshowHeartbeat(input: HeartbeatIdentity): Promise<AirshowHeartbeatRun> {
  const value = await apiClient<unknown>(`${base(input.projectId)}/${encodeURIComponent(input.runId)}/cancel`, {
    method: "POST", signal: input.signal,
  });
  return decode(() => parseAirshowHeartbeatRun(value, { projectId: input.projectId, runId: input.runId }));
}

export async function fetchAirshowHeartbeatReport(input: HeartbeatIdentity): Promise<AirshowHeartbeatReport> {
  const value = await apiClient<unknown>(`${base(input.projectId)}/${encodeURIComponent(input.runId)}/report`, {
    method: "GET", signal: input.signal,
  });
  return decode(() => parseAirshowHeartbeatReport(value, { projectId: input.projectId, runId: input.runId }));
}

export async function fetchAirshowHeartbeatModels(input: HeartbeatIdentity & {
  cursor: number; limit: number;
}): Promise<AirshowHeartbeatModelsPage> {
  if (!Number.isInteger(input.cursor) || input.cursor < 0 || !Number.isInteger(input.limit)
      || input.limit < 1 || input.limit > 100) throw new Error("Airshow heartbeat model page is invalid");
  const query = `?cursor=${input.cursor}&limit=${input.limit}`;
  const value = await apiClient<unknown>(`${base(input.projectId)}/${encodeURIComponent(input.runId)}/models${query}`, {
    method: "GET", signal: input.signal,
  });
  return decode(() => parseAirshowHeartbeatModelsPage(value, { projectId: input.projectId, runId: input.runId }));
}

export const airshowHeartbeatClient: AirshowHeartbeatClient = {
  current: fetchCurrentAirshowHeartbeat,
  status: fetchAirshowHeartbeatStatus,
  start: startAirshowHeartbeat,
  cancel: cancelAirshowHeartbeat,
  report: fetchAirshowHeartbeatReport,
  models: fetchAirshowHeartbeatModels,
};
